import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're NOT already on the login page
      // and if we actually have a token (meaning it's an auth failure, not a login attempt)
      const currentPath = window.location.pathname;
      const hasToken = localStorage.getItem('token');

      if (currentPath !== '/login' && hasToken) {
        // Token expired or invalid - clear and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  // The second step of signing in, with the challenge the password step gave.
  loginSetup: async (challenge) => (await api.post('/auth/login/setup', { challenge })).data,
  loginSetupConfirm: async (challenge, code) => (await api.post('/auth/login/setup/confirm', { challenge, code })).data,
  loginVerify: async (challenge, code) => (await api.post('/auth/login/verify', { challenge, code })).data,
  twoFactorStatus: async () => (await api.get('/auth/2fa')).data,
  newRecoveryCodes: async (code) => (await api.post('/auth/2fa/recovery-codes', { code })).data,

  register: async (username, email, password, fullName) => {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      fullName,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updatePreferences: async (preferences) => {
    const response = await api.put('/auth/preferences', preferences);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// Metrics API
export const metricsAPI = {
  getLatestAll: async (companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get('/metrics/data/latest/all', { params });
    return response.data;
  },

  getLatest: async (metricKey, companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get(`/metrics/data/${metricKey}/latest`, { params });
    return response.data;
  },

  getHistory: async (metricKey, params = {}) => {
    const response = await api.get(`/metrics/data/${metricKey}/history`, {
      params,
    });
    return response.data;
  },

  getDefinitions: async (companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get('/metrics/definitions', { params });
    return response.data;
  },

  submitMetric: async (metricKey, value, timestamp) => {
    const response = await api.post('/metrics/data', {
      metricKey,
      value,
      timestamp,
    });
    return response.data;
  },
};

// Dashboards API
export const dashboardsAPI = {
  getAll: async () => {
    const response = await api.get('/dashboards');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/dashboards/${id}`);
    return response.data;
  },

  create: async (name, companyId, isDefault = false, staleThresholdMinutes = null) => {
    const response = await api.post('/dashboards', {
      name,
      companyId,
      isDefault,
      staleThresholdMinutes,
      layout: { cols: 12, rowHeight: 100 }
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/dashboards/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/dashboards/${id}`);
    return response.data;
  },

  addWidget: async (dashboardId, metricKey, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      metricKey,
      position,
      config,
    });
    return response.data;
  },

  addDatasetWidget: async (dashboardId, datasetName, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      widgetKind: 'dataset',
      datasetName,
      position,
      config,
    });
    return response.data;
  },

  addReportWidget: async (dashboardId, reportId, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      widgetKind: 'dataset',
      reportId,
      position,
      config,
    });
    return response.data;
  },

  addKpiWidget: async (dashboardId, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      widgetKind: 'kpi',
      position,
      config,
    });
    return response.data;
  },

  addStatementWidget: async (dashboardId, datasetName, layoutKey, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      widgetKind: 'statement',
      position,
      config: { inheritDateRange: true, ...config, datasetName, layoutKey },
    });
    return response.data;
  },

  updateWidget: async (dashboardId, widgetId, data) => {
    const response = await api.put(
      `/dashboards/${dashboardId}/widgets/${widgetId}`,
      data
    );
    return response.data;
  },

  updateWidgetPositions: async (dashboardId, widgets) => {
    const response = await api.put(
      `/dashboards/${dashboardId}/widgets/positions`,
      { widgets }
    );
    return response.data;
  },

  deleteWidget: async (dashboardId, widgetId) => {
    const response = await api.delete(
      `/dashboards/${dashboardId}/widgets/${widgetId}`
    );
    return response.data;
  },
};

// Companies API
export const companiesAPI = {
  getAll: async () => {
    const response = await api.get('/companies');
    return response.data;
  },

  getMyCompanies: async () => {
    const response = await api.get('/companies/my-companies');
    return response.data;
  },

  getById: async (companyId) => {
    const response = await api.get(`/companies/${companyId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/companies', data);
    return response.data;
  },

  update: async (companyId, data) => {
    const response = await api.put(`/companies/${companyId}`, data);
    return response.data;
  },

  updateSettings: async (companyId, settings) => {
    const response = await api.patch(`/companies/${companyId}/settings`, settings);
    return response.data;
  },

  delete: async (companyId) => {
    const response = await api.delete(`/companies/${companyId}`);
    return response.data;
  },

  getUsers: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/users`);
    return response.data;
  },

  assignUser: async (companyId, userId) => {
    const response = await api.post(`/companies/${companyId}/users/${userId}`);
    return response.data;
  },

  removeUser: async (companyId, userId) => {
    const response = await api.delete(`/companies/${companyId}/users/${userId}`);
    return response.data;
  },
};

// Datasets API (BI / report layer)
export const datasetsAPI = {
  list: async (companyId) => {
    const response = await api.get('/datasets', { params: { companyId } });
    return response.data;
  },

  getSchema: async (datasetName, companyId) => {
    const response = await api.get(`/datasets/${datasetName}/schema`, {
      params: { companyId },
    });
    return response.data;
  },

  query: async (datasetName, companyId, options = {}) => {
    const response = await api.get(`/datasets/${datasetName}/query`, {
      params: { companyId, ...options },
    });
    return response.data;
  },

  delete: async (datasetName, companyId) => {
    const response = await api.delete(`/datasets/${datasetName}`, { params: { companyId } });
    return response.data;
  },

  setTemplate: async (datasetName, companyId, template) => {
    const response = await api.put(`/datasets/${datasetName}/template?companyId=${companyId}`, { template });
    return response.data;
  },

  /** Define a dataset before any data arrives: { name, title, description, columns, primaryKey, relationships, cadence }. */
  define: async (companyId, definition) => (await api.post(`/datasets?companyId=${companyId}`, definition)).data,
  updateDefinition: async (datasetName, companyId, definition) =>
    (await api.put(`/datasets/${datasetName}/definition?companyId=${companyId}`, definition)).data,

  // Uploads from the app use the same load sessions as sync tools, with the admin's login.
  beginLoad: async (datasetName, companyId, body) => (await api.post(`/datasets/${datasetName}/loads?companyId=${companyId}`, body)).data,
  appendRows: async (datasetName, companyId, loadId, rows) =>
    (await api.post(`/datasets/${datasetName}/loads/${loadId}/rows?companyId=${companyId}`, { rows })).data,
  commitLoad: async (datasetName, companyId, loadId) => (await api.post(`/datasets/${datasetName}/loads/${loadId}/commit?companyId=${companyId}`)).data,
  abortLoad: async (datasetName, companyId, loadId) => (await api.delete(`/datasets/${datasetName}/loads/${loadId}`, { params: { companyId } })).data,
};

export const reportsAPI = {
  getMeta: async (companyId) => {
    const response = await api.get('/reports/meta', { params: { companyId } });
    return response.data;
  },

  query: async (companyId, query) => {
    const response = await api.post('/reports/query', { companyId, query });
    return response.data;
  },

  sql: async (companyId, query) => {
    const response = await api.post('/reports/sql', { companyId, query });
    return response.data;
  },

  library: async (companyId) => (await api.get('/reports/library', { params: { companyId } })).data,
};

export const templatesAPI = {
  list: async () => (await api.get('/templates')).data,
  get: async (name, version) => (await api.get(`/templates/${name}`, { params: version ? { version } : {} })).data,
};

export const packsAPI = {
  list: async (companyId) => (await api.get('/packs', { params: { companyId } })).data,
  get: async (id, companyId) => (await api.get(`/packs/${id}`, { params: { companyId } })).data,
  install: async (id, companyId, body) => (await api.post(`/packs/${id}/install?companyId=${companyId}`, body)).data,
  update: async (id, companyId, body) => (await api.post(`/packs/${id}/update?companyId=${companyId}`, body)).data,
  agentKey: async (id, companyId) => (await api.post(`/packs/${id}/agent-key?companyId=${companyId}`)).data,
  uninstall: async (id, companyId, deleteData = false) =>
    (await api.delete(`/packs/${id}`, { params: { companyId, ...(deleteData ? { deleteData: 'true' } : {}) } })).data,
};

export const connectionsAPI = {
  get: async (connector, companyId) => (await api.get(`/connections/${connector}`, { params: { companyId } })).data,
  connectWithCredentials: async (connector, companyId, fields) => (await api.post(`/connections/${connector}/credentials?companyId=${companyId}`, fields)).data,
  connectWithToken: async (connector, companyId, accessToken, currentPassword) => (await api.post(`/connections/${connector}/token?companyId=${companyId}`, { accessToken, currentPassword })).data,
  startOAuth: async (connector, companyId, app) => (await api.post(`/connections/${connector}/oauth/start?companyId=${companyId}`, app)).data,
  completeOAuth: async (connector, body) => (await api.post(`/connections/${connector}/oauth/complete`, body)).data,
  options: async (connector, companyId) => (await api.get(`/connections/${connector}/options`, { params: { companyId } })).data,
  health: async (connector, companyId) => (await api.get(`/connections/${connector}/health`, { params: { companyId } })).data,
  previewDefinition: async (connector, companyId, concept, rule) => (await api.post(`/connections/${connector}/definitions/preview?companyId=${companyId}`, { concept, rule })).data,
  saveSettings: async (connector, companyId, body) => (await api.put(`/connections/${connector}/settings?companyId=${companyId}`, body)).data,
  sync: async (connector, companyId, full = false) => (await api.post(`/connections/${connector}/sync?companyId=${companyId}`, { full })).data,
  disconnect: async (connector, companyId, currentPassword) => (await api.delete(`/connections/${connector}`, { params: { companyId }, data: { currentPassword } })).data,
  audit: async (connector, companyId) => (await api.get(`/connections/${connector}/audit`, { params: { companyId } })).data,
};

export const measuresAPI = {
  list: async (datasetName, companyId) => (await api.get(`/datasets/${datasetName}/measures`, { params: { companyId } })).data,
  validate: async (datasetName, companyId, measure) => (await api.post(`/datasets/${datasetName}/measures/validate?companyId=${companyId}`, measure)).data,
  create: async (datasetName, companyId, measure) => (await api.post(`/datasets/${datasetName}/measures?companyId=${companyId}`, measure)).data,
  update: async (datasetName, companyId, id, patch) => (await api.put(`/datasets/${datasetName}/measures/${id}?companyId=${companyId}`, patch)).data,
  delete: async (datasetName, companyId, id) => (await api.delete(`/datasets/${datasetName}/measures/${id}`, { params: { companyId } })).data,
};

export const kpisAPI = {
  list: async (companyId) => (await api.get('/kpis', { params: { companyId } })).data,
  get: async (id, companyId) => (await api.get(`/kpis/${id}`, { params: { companyId } })).data,
  suggested: async (companyId) => (await api.get('/kpis/suggested', { params: { companyId } })).data,
  evaluate: async (companyId, ids) => (await api.get('/kpis/evaluate', { params: { companyId, ...(ids && ids.length ? { ids: ids.join(',') } : {}) } })).data,
  evaluateOne: async (id, companyId) => (await api.get(`/kpis/${id}/evaluate`, { params: { companyId } })).data,
  create: async (companyId, kpi) => (await api.post(`/kpis?companyId=${companyId}`, kpi)).data,
  adopt: async (companyId, datasetName, keys) => (await api.post(`/kpis/adopt?companyId=${companyId}`, { datasetName, keys })).data,
  update: async (id, companyId, patch) => (await api.put(`/kpis/${id}?companyId=${companyId}`, patch)).data,
  setTargets: async (id, companyId, targets) => (await api.put(`/kpis/${id}/targets?companyId=${companyId}`, { targets })).data,
  delete: async (id, companyId) => (await api.delete(`/kpis/${id}`, { params: { companyId } })).data,
};

export const savedReportsAPI = {
  list: async (companyId) => (await api.get('/saved-reports', { params: { companyId } })).data,
  get: async (id, companyId) => (await api.get(`/saved-reports/${id}`, { params: { companyId } })).data,
  run: async (id, companyId, dateRange, filters) => (await api.get(`/saved-reports/${id}/run`, {
    params: { companyId, ...(dateRange ? { dateRange } : {}), ...(filters?.length ? { filters: JSON.stringify(filters) } : {}) },
  })).data,
  preview: async (companyId, draft, limit) => (await api.post(`/saved-reports/preview?companyId=${companyId}`, { ...draft, limit })).data,
  create: async (companyId, report) => (await api.post(`/saved-reports?companyId=${companyId}`, report)).data,
  update: async (id, companyId, patch) => (await api.put(`/saved-reports/${id}?companyId=${companyId}`, patch)).data,
  delete: async (id, companyId) => (await api.delete(`/saved-reports/${id}`, { params: { companyId } })).data,
};

export const alertsAPI = {
  rules: async (companyId) => (await api.get('/alerts/rules', { params: { companyId } })).data,
  get: async (id, companyId) => (await api.get(`/alerts/rules/${id}`, { params: { companyId } })).data,
  create: async (companyId, rule) => (await api.post(`/alerts/rules?companyId=${companyId}`, rule)).data,
  update: async (id, companyId, patch) => (await api.put(`/alerts/rules/${id}?companyId=${companyId}`, patch)).data,
  delete: async (id, companyId) => (await api.delete(`/alerts/rules/${id}`, { params: { companyId } })).data,
  active: async (companyId) => (await api.get('/alerts/active', { params: { companyId } })).data,
  events: async (companyId, limit = 50) => (await api.get('/alerts/events', { params: { companyId, limit } })).data,
  acknowledge: async (id, companyId) => (await api.post(`/alerts/rules/${id}/acknowledge?companyId=${companyId}`)).data,
  evaluate: async (id, companyId) => (await api.post(`/alerts/rules/${id}/evaluate?companyId=${companyId}`)).data,
};

export const dashboardTemplatesAPI = {
  list: async (companyId) => (await api.get('/dashboards/templates', { params: { companyId } })).data,
  create: async (key, companyId, name) => (await api.post(`/dashboards/templates/${key}?companyId=${companyId}`, name ? { name } : {})).data,
};

export const rollupsAPI = {
  set: async (definitionId, rollupAggregation, rollupCadence) => (await api.put(`/admin/metrics/definitions/${definitionId}`, { rollupAggregation, rollupCadence })).data,
  run: async (definitionId) => (await api.post(`/admin/metrics/definitions/${definitionId}/rollup`)).data,
};

export const statementsAPI = {
  list: async (companyId) => (await api.get('/statements', { params: { companyId } })).data,
  run: async (datasetName, layoutKey, companyId, { period, budgetVersion } = {}) => (await api.get(`/statements/${encodeURIComponent(datasetName)}/${encodeURIComponent(layoutKey)}`, {
    params: { companyId, ...(period ? { period } : {}), ...(budgetVersion ? { budgetVersion } : {}) },
  })).data,
};

export default api;
